/**
 * Assignments — V98-1-1 / ADR-0046 (migration 230).
 *
 * An assignment says who holds which hat (`responsibility`) on which item
 * (`subject_kind` × `subject_id`) in a workspace, for a window
 * (`effective_from` … `effective_to`, open-ended when NULL). Assignments are
 * separate from permission roles: roles gate workspace capabilities,
 * assignments gate acting on a specific item (`requireAssignmentOrRole`,
 * packages/cloudflare/src/lib/assignment-guard.ts).
 *
 * A person's hats are the union of their current assignments (`listHats`) —
 * there is no role switcher. Every change writes an append-only
 * `responsibility_assignment_log` row in the same batch as the change; rows
 * in `assignments` are ended (effective_to), never deleted.
 *
 * T17 (ADR-0046 Appendix A; ADR-0047): an identity on an audit-firm
 * engagement can never be given a client hat. The roster is the firm-tier
 * roster (migration 240, read through firm-tier-repository `isRosterIdentity`),
 * plus a legacy v9.6 `assessor_engagements.assessor_user_id` not revoked or
 * revoked within the last 12 months. A refused attempt is logged as
 * `rejected` and written to the firm chain.
 *
 * Not here yet (later V98-1 rows): SoD pair re-checks on assign/reassign
 * (V98-1-9), delegation / absence (V98-1-10), program-lead scoping of who may
 * assign — program leads manage their programs' subjects in the route
 * (V98-2-1, `programsOfSubject`).
 */
import { getProgramManifest } from '../programs/manifests.js';
import { isRosterIdentity, recordIndependenceRejection } from './firm-tier-repository.js';
import { generateRandomId } from '../utils/audit-crypto.js';
// Runtime-only use (function bodies) — these modules import this one too.
import { createWorkItem } from './work-item-service.js';
import { sodEventStatement } from './sod-service.js';
export const ASSIGNMENT_SUBJECT_KINDS = [
    'program', 'period', 'requirement', 'control', 'system', 'system_control',
    'policy', 'subservice_org', 'obligation', 'scn', 'finding', 'access_review',
    'external_service', 'risk', 'register_item', 'work_item',
    // Migration 330a / V103-2-3 (ADR-0065 §1): one program's control record.
    'program_control',
];
export const ASSIGNMENT_RESPONSIBILITIES = [
    'owner', 'approver', 'backup_approver', 'legal_reviewer', 'system_owner',
    'attester', 'access_reviewer', 'checker', 'program_lead',
];
/**
 * V98-7-3 H1: hats that carry privilege or sign-off authority. Granting,
 * moving or ending one is owner/admin-only (like `program_lead`; enforced by
 * the routes through `ADMIN_ONLY_HATS`), and nobody — owners and admins
 * included — may give one to themselves (`self_grant`, enforced here in
 * `assign` / `reassignHats` so every caller gets it). The one exception is a
 * solo workspace (`soloSelfGrantAllowed`): logged, and it opens a
 * compensating requirement (ADR-0046 §6 independent party, UX-9).
 */
export const SENSITIVE_HATS = new Set([
    'legal_reviewer', 'approver', 'backup_approver', 'access_reviewer', 'attester',
]);
/** Hats only an owner/admin may grant, move or end (program leads can't). */
export const ADMIN_ONLY_HATS = new Set([...SENSITIVE_HATS, 'program_lead']);
/** Hats whose current holders are told (work item, no privileged content) when the hat is added, moved or ended by someone else. */
export const MOVE_NOTICE_HATS = new Set(['legal_reviewer', 'approver']);
/** Work-item source kinds written here. */
export const HAT_CHANGE_NOTICE = 'hat_change_notice';
export const SOLO_SELF_GRANT = 'solo_self_grant';
// ---- errors --------------------------------------------------------------------
export class AssignmentValidationError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'AssignmentValidationError';
    }
}
export class AssignmentNotFoundError extends Error {
    what;
    constructor(what, id) {
        super(`${what === 'assignment' ? 'Assignment' : 'Subject'} not found: ${id}`);
        this.what = what;
        this.name = 'AssignmentNotFoundError';
    }
}
export class AssignmentConflictError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AssignmentConflictError';
    }
}
/** T17: the user is on an audit-firm engagement and can't hold a client hat. */
export class AssignmentRejectedError extends Error {
    code = 'audit_firm_member';
    constructor(message = 'This person is on an audit-firm engagement and cannot hold a client responsibility.') {
        super(message);
        this.name = 'AssignmentRejectedError';
    }
}
/** H1: a sensitive hat given to the person granting it (403 `self_grant`). */
export class AssignmentForbiddenError extends Error {
    code = 'self_grant';
    constructor(message = 'Nobody can grant themselves an approval, legal-review, access-review or attester hat — another owner or admin must grant it.') {
        super(message);
        this.name = 'AssignmentForbiddenError';
    }
}
// ---- helpers -------------------------------------------------------------------
const KIND_SET = new Set(ASSIGNMENT_SUBJECT_KINDS);
const RESP_SET = new Set(ASSIGNMENT_RESPONSIBILITIES);
const REASON_MAX = 2000;
const REASSIGN_MAX_SUBJECTS = 100;
/** ADR-0047: roster membership within this many months still excludes. */
const INDEPENDENCE_LOOKBACK_MONTHS = 12;
export function isAssignmentSubjectKind(v) {
    return typeof v === 'string' && KIND_SET.has(v);
}
export function isAssignmentResponsibility(v) {
    return typeof v === 'string' && RESP_SET.has(v);
}
function validateSubject(subject) {
    if (!isAssignmentSubjectKind(subject?.kind)) {
        throw new AssignmentValidationError('invalid_subject_kind', `Unknown subject kind: ${String(subject?.kind)}`);
    }
    if (typeof subject.id !== 'string' || subject.id.length < 1 || subject.id.length > 200) {
        throw new AssignmentValidationError('invalid_subject_id', 'subject id must be 1–200 characters');
    }
}
function toIso(v, field) {
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime()))
        throw new AssignmentValidationError('invalid_date', `${field} is not a valid date`);
    return d.toISOString();
}
function cleanReason(reason, required) {
    const r = (reason ?? '').trim();
    if (!r) {
        if (required)
            throw new AssignmentValidationError('reason_required', 'A reason is required');
        return null;
    }
    if (r.length > REASON_MAX)
        throw new AssignmentValidationError('reason_too_long', `reason must be at most ${REASON_MAX} characters`);
    return r;
}
const ACTIVE_SQL = `effective_from <= ? AND (effective_to IS NULL OR effective_to > ?)`;
/**
 * Table-backed subject kinds: the subject must exist in this workspace.
 * `control` is the shared catalogue (workspace-independent). `program` is a
 * certification profile (the program row, ADR-0048 / migration 233).
 * `external_service` is the V98-2-3 register (migration 234).
 */
const SUBJECT_LOOKUP = {
    program: `SELECT 1 AS ok FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? LIMIT 1`,
    period: `SELECT 1 AS ok FROM audit_periods WHERE period_id = ? AND workspace_id = ? LIMIT 1`,
    system: `SELECT 1 AS ok FROM ai_systems s JOIN customer_profiles p ON p.profile_id = s.profile_id
            WHERE s.system_id = ? AND p.workspace_id = ? LIMIT 1`,
    system_control: `SELECT 1 AS ok FROM system_control_states WHERE id = ? AND workspace_id = ? LIMIT 1`,
    policy: `SELECT 1 AS ok FROM policy_versions WHERE policy_key = ? AND workspace_id = ? LIMIT 1`,
    subservice_org: `SELECT 1 AS ok FROM subservice_orgs WHERE subservice_id = ? AND workspace_id = ? LIMIT 1`,
    obligation: `SELECT 1 AS ok FROM recurring_obligations WHERE obligation_id = ? AND workspace_id = ? LIMIT 1`,
    scn: `SELECT 1 AS ok FROM significant_changes WHERE change_id = ? AND workspace_id = ? LIMIT 1`,
    finding: `SELECT 1 AS ok FROM findings WHERE finding_id = ? AND workspace_id = ? LIMIT 1`,
    access_review: `SELECT 1 AS ok FROM access_reviews WHERE review_id = ? AND workspace_id = ? LIMIT 1`,
    risk: `SELECT 1 AS ok FROM security_risks WHERE risk_id = ? AND workspace_id = ? LIMIT 1`,
    register_item: `SELECT 1 AS ok FROM customer_register_items ri JOIN customer_profiles p ON p.profile_id = ri.profile_id
                   WHERE ri.id = ? AND p.workspace_id = ? LIMIT 1`,
    // Migration 231 / V98-1-4.
    work_item: `SELECT 1 AS ok FROM work_items WHERE work_item_id = ? AND workspace_id = ? LIMIT 1`,
    // Migration 330b / V103-2-3: a program control record (rows exist from the first edit).
    program_control: `SELECT 1 AS ok FROM program_controls WHERE program_control_id = ? AND workspace_id = ? LIMIT 1`,
    // Migration 234 / V98-2-3.
    external_service: `SELECT 1 AS ok FROM external_services WHERE external_service_id = ? AND workspace_id = ? LIMIT 1`,
};
/** Catalogue-level subjects (not workspace-owned rows). */
const GLOBAL_SUBJECT_LOOKUP = {
    requirement: `SELECT 1 AS ok FROM framework_requirements WHERE requirement_id = ? LIMIT 1`,
    control: `SELECT 1 AS ok FROM controls WHERE control_id = ? LIMIT 1`,
};
/** True when the subject exists in the workspace (or its kind has no backing table yet). */
export async function subjectExists(db, workspaceId, subject) {
    const global = GLOBAL_SUBJECT_LOOKUP[subject.kind];
    if (global)
        return (await db.query(global, [subject.id])).results.length > 0;
    const sql = SUBJECT_LOOKUP[subject.kind];
    if (!sql)
        return true;
    const r = await db.query(sql, [subject.id, workspaceId]);
    return r.results.length > 0;
}
/**
 * T17 roster check (ADR-0046 T17 / ADR-0047 R2): is this identity on an
 * audit-firm engagement roster now, or within that engagement's independence
 * lookback (12 months by default)? Identity is `user_id` + the bound
 * `workos_user_id` (firm-tier-repository `isRosterIdentity`), never the email
 * domain. A legacy v9.6 single-assessor engagement (`assessor_user_id`, live
 * or revoked within 12 months) still counts.
 */
export async function isAuditFirmIdentity(db, userId, now = new Date()) {
    if (await isRosterIdentity(db, userId, now))
        return true;
    const cutoff = new Date(now);
    cutoff.setUTCMonth(cutoff.getUTCMonth() - INDEPENDENCE_LOOKBACK_MONTHS);
    const r = await db.query(`SELECT engagement_id FROM assessor_engagements
      WHERE assessor_user_id = ? AND (revoked_at IS NULL OR revoked_at >= ?) LIMIT 1`, [userId, cutoff.toISOString()]);
    return r.results.length > 0;
}
function logStatement(e, newId) {
    return {
        sql: `INSERT INTO responsibility_assignment_log
            (log_id, workspace_id, assignment_id, event, subject_kind, subject_id, responsibility,
             user_id, actor_user_id, reason, before_json, after_json, at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [
            e.log_id ?? newId(), e.workspace_id, e.assignment_id, e.event, e.subject_kind, e.subject_id, e.responsibility,
            e.user_id, e.actor_user_id, e.reason, e.before_json, e.after_json, e.at,
        ],
    };
}
function insertStatement(a, createdAt) {
    return {
        sql: `INSERT INTO assignments
            (assignment_id, workspace_id, subject_kind, subject_id, responsibility, user_id, effective_from, effective_to, assigned_by, created_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [
            a.assignment_id, a.workspace_id, a.subject_kind, a.subject_id, a.responsibility, a.user_id,
            a.effective_from, a.effective_to, a.assigned_by, createdAt,
        ],
    };
}
const snapshot = (a) => JSON.stringify({ assignment_id: a.assignment_id, user_id: a.user_id, effective_from: a.effective_from, effective_to: a.effective_to });
async function loadAssignment(db, workspaceId, assignmentId) {
    const r = await db.query(`SELECT * FROM assignments WHERE assignment_id = ? AND workspace_id = ? LIMIT 1`, [assignmentId, workspaceId]);
    if (!r.results[0])
        throw new AssignmentNotFoundError('assignment', assignmentId);
    return r.results[0];
}
async function isActiveMember(db, workspaceId, userId) {
    const r = await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, userId]);
    return r.results.length > 0;
}
const HAT_LABEL = {
    legal_reviewer: 'legal reviewer', approver: 'approver', backup_approver: 'backup approver',
    access_reviewer: 'access reviewer', attester: 'attester', program_lead: 'program lead',
};
/**
 * H1 solo-workspace exception: the actor is an owner/admin and there is no
 * other active member who could hold the hat (audit-firm identities don't
 * count — they can never hold a client hat).
 */
export async function soloSelfGrantAllowed(db, workspaceId, actorUserId, now = new Date()) {
    const me = await db.query(`SELECT role FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, actorUserId]);
    if (!me.results[0] || !['owner', 'admin'].includes(me.results[0].role))
        return false;
    const others = await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND user_id <> ? AND removed_at IS NULL`, [workspaceId, actorUserId]);
    for (const o of others.results) {
        if (!(await isAuditFirmIdentity(db, o.user_id, now)))
            return false;
    }
    return true;
}
/**
 * H1 gate: 'ok' unless the target is the actor and a sensitive hat is
 * involved; then 'solo' (allowed, recorded) in a solo workspace, else throws
 * `AssignmentForbiddenError`. Evaluated at the real present.
 */
export async function selfGrantGate(db, workspaceId, actorUserId, targetUserId, hats) {
    if (actorUserId !== targetUserId || !hats.some((h) => SENSITIVE_HATS.has(h)))
        return 'ok';
    if (await soloSelfGrantAllowed(db, workspaceId, actorUserId))
        return 'solo';
    throw new AssignmentForbiddenError();
}
async function subjectLabel(db, workspaceId, subject) {
    if (subject.kind === 'program') {
        const r = await db.query(`SELECT program FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? LIMIT 1`, [subject.id, workspaceId]);
        const p = r.results[0]?.program;
        if (p)
            return `the ${getProgramManifest(p)?.shortLabel ?? p} program`;
    }
    return `a ${subject.kind.replace(/_/g, ' ')}`;
}
async function displayName(db, userId) {
    if (userId.startsWith('system:'))
        return 'the system';
    const r = await db.query(`SELECT display_name, email FROM users WHERE user_id = ? LIMIT 1`, [userId]);
    return r.results[0]?.display_name || r.results[0]?.email || 'a member';
}
/**
 * H1: tell holders of a legal_reviewer / approver hat that it was added to
 * someone else, moved away from them, or ended by someone else — a work item
 * on their owed list (titles name the hat, the program and people only;
 * nothing privileged). Best-effort: a holder who can no longer receive items
 * is skipped.
 */
async function noticeHatChange(db, input) {
    if (!MOVE_NOTICE_HATS.has(input.responsibility))
        return;
    const recipients = [...new Set(input.notify)].filter((u) => u !== input.actor_user_id && !u.startsWith('system:'));
    if (recipients.length === 0)
        return;
    const where = await subjectLabel(db, input.workspace_id, input.subject);
    const hat = HAT_LABEL[input.responsibility] ?? input.responsibility;
    const other = input.other_user_id ? await displayName(db, input.other_user_id) : null;
    const actor = await displayName(db, input.actor_user_id);
    const title = input.change === 'added' ? `Another ${hat} was added on ${where}: ${other}`
        : input.change === 'moved' ? `Your ${hat} hat on ${where} moved to ${other}`
            : `Your ${hat} hat on ${where} was ended`;
    for (const u of recipients) {
        try {
            await createWorkItem(db, {
                workspace_id: input.workspace_id, kind: 'task', title: title.slice(0, 300),
                detail: `Changed by ${actor}. If you didn’t expect this, ask an owner or admin.`,
                source_kind: HAT_CHANGE_NOTICE, source_id: input.assignment_id, owner_user_id: u, actor_user_id: input.actor_user_id, now: input.now,
            });
        }
        catch (err) {
            if (err instanceof AssignmentValidationError || err instanceof AssignmentRejectedError)
                continue;
            throw err;
        }
    }
}
/**
 * H1 solo exception record: an append-only sod_events row and a compensating
 * requirement on the actor's owed list — add an independent party (advisor
 * seat / board contact, ADR-0046 §6) who reviews the self-held hat. Closed
 * when a seat is added (coverage-service `addIndependentParty`).
 */
async function recordSoloSelfGrant(db, a, now) {
    const where = await subjectLabel(db, a.workspace_id, { kind: a.subject_kind, id: a.subject_id });
    const hat = HAT_LABEL[a.responsibility] ?? a.responsibility;
    const wi = await createWorkItem(db, {
        workspace_id: a.workspace_id, kind: 'task',
        title: `Add an independent party · you hold the ${hat} hat on ${where} that you granted yourself`.slice(0, 300),
        detail: 'You are the only member, so this was allowed and logged. An advisor seat or board contact outside your reporting line reviews it (compensating control).',
        source_kind: SOLO_SELF_GRANT, source_id: a.assignment_id, owner_user_id: a.user_id, actor_user_id: a.user_id, now,
    });
    await db.batch([sodEventStatement({
            workspace_id: a.workspace_id, transition: 'config', event: SOLO_SELF_GRANT, actor_user_id: a.user_id,
            record_kind: 'assignment', record_id: a.assignment_id, item_kind: a.subject_kind, item_id: a.subject_id,
            detail: { responsibility: a.responsibility, compensating_work_item_id: wi.work_item_id }, created_at: now.toISOString(),
        })]);
}
/**
 * Who may receive a hat: an active member of the workspace who is not on an
 * audit-firm engagement (T17). A T17 refusal is logged before throwing.
 */
export async function assertAssignable(db, input) {
    if (!(await isActiveMember(db, input.workspace_id, input.user_id))) {
        throw new AssignmentValidationError('not_member', 'The assignee must be an active member of the workspace');
    }
    const now = input.now ?? new Date();
    if (await isAuditFirmIdentity(db, input.user_id, now)) {
        const newId = input.newId ?? (() => generateRandomId('asl'));
        const at = now.toISOString();
        await db.batch(input.subjects.map(({ subject, responsibility }) => logStatement({
            workspace_id: input.workspace_id, assignment_id: null, event: 'rejected',
            subject_kind: subject.kind, subject_id: subject.id, responsibility,
            user_id: input.user_id, actor_user_id: input.actor_user_id,
            reason: 'audit_firm_member', before_json: null, after_json: null, at,
        }, newId)));
        // ADR-0047 R4: the rejected attempt is also written to the firm chain(s).
        await recordIndependenceRejection(db, {
            user_id: input.user_id, point: 'assignment', attempted_by: input.actor_user_id, target_workspace_id: input.workspace_id, now,
        });
        throw new AssignmentRejectedError();
    }
}
// ---- reads ---------------------------------------------------------------------
/** The caller's hats: every assignment in force for them in this workspace. */
export async function listHats(db, workspaceId, userId, now = new Date()) {
    const iso = now.toISOString();
    const r = await db.query(`SELECT * FROM assignments WHERE workspace_id = ? AND user_id = ? AND ${ACTIVE_SQL}
      ORDER BY responsibility ASC, subject_kind ASC, subject_id ASC`, [workspaceId, userId, iso, iso]);
    const responsibilities = [...new Set(r.results.map((a) => a.responsibility))].sort();
    return { assignments: r.results, responsibilities };
}
/** Assignments on one subject; current only unless `includeEnded`. */
export async function listAssignments(db, workspaceId, subject, opts = {}) {
    validateSubject(subject);
    const iso = (opts.now ?? new Date()).toISOString();
    const r = opts.includeEnded
        ? await db.query(`SELECT * FROM assignments WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ?
          ORDER BY effective_from ASC, assignment_id ASC`, [workspaceId, subject.kind, subject.id])
        : await db.query(`SELECT * FROM assignments WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ? AND ${ACTIVE_SQL}
          ORDER BY responsibility ASC, user_id ASC`, [workspaceId, subject.kind, subject.id, iso, iso]);
    return r.results;
}
/** Does the user hold any of these hats on the subject right now? */
export async function hasAssignment(db, workspaceId, subject, userId, responsibilities, now = new Date()) {
    if (responsibilities.length === 0)
        return false;
    const iso = now.toISOString();
    // d1-in-list: bounded — responsibilities is a subset of the ASSIGNMENT_RESPONSIBILITIES enum
    const r = await db.query(`SELECT assignment_id FROM assignments
      WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ? AND user_id = ?
        AND responsibility IN (${responsibilities.map(() => '?').join(', ')})
        AND ${ACTIVE_SQL}
      LIMIT 1`, [workspaceId, subject.kind, subject.id, userId, ...responsibilities, iso, iso]);
    return r.results.length > 0;
}
/** The append-only change log for one subject, oldest first. */
export async function listAssignmentLog(db, workspaceId, subject) {
    validateSubject(subject);
    const r = await db.query(`SELECT * FROM responsibility_assignment_log WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ?
      ORDER BY at ASC, log_id ASC`, [workspaceId, subject.kind, subject.id]);
    return r.results;
}
/** T-assign: give a member a hat on a subject (T17-checked, logged). */
export async function assign(db, input) {
    validateSubject(input.subject);
    if (!isAssignmentResponsibility(input.responsibility)) {
        throw new AssignmentValidationError('invalid_responsibility', `Unknown responsibility: ${String(input.responsibility)}`);
    }
    const reason = cleanReason(input.reason, false);
    const now = input.now ?? new Date();
    const from = input.effective_from != null ? toIso(input.effective_from, 'effective_from') : now.toISOString();
    const to = input.effective_to != null ? toIso(input.effective_to, 'effective_to') : null;
    if (to !== null && to <= from) {
        throw new AssignmentValidationError('invalid_window', 'effective_to must be after effective_from');
    }
    if (!(await subjectExists(db, input.workspace_id, input.subject))) {
        throw new AssignmentNotFoundError('subject', `${input.subject.kind}:${input.subject.id}`);
    }
    const newId = input.newId ?? (() => generateRandomId('asg'));
    const logId = input.newId ?? (() => generateRandomId('asl'));
    await assertAssignable(db, {
        workspace_id: input.workspace_id, user_id: input.user_id, actor_user_id: input.actor_user_id,
        subjects: [{ subject: input.subject, responsibility: input.responsibility }], now, newId: logId,
    });
    const gate = await selfGrantGate(db, input.workspace_id, input.actor_user_id, input.user_id, [input.responsibility]);
    const priorHolders = MOVE_NOTICE_HATS.has(input.responsibility)
        ? (await listAssignments(db, input.workspace_id, input.subject, { now })).filter((h) => h.responsibility === input.responsibility).map((h) => h.user_id)
        : [];
    const dup = await db.query(`SELECT assignment_id FROM assignments
      WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ? AND responsibility = ? AND user_id = ? AND effective_to IS NULL
      LIMIT 1`, [input.workspace_id, input.subject.kind, input.subject.id, input.responsibility, input.user_id]);
    if (dup.results[0]) {
        throw new AssignmentConflictError(`${input.user_id} already holds ${input.responsibility} on ${input.subject.kind}:${input.subject.id}`);
    }
    const row = {
        assignment_id: newId(), workspace_id: input.workspace_id, subject_kind: input.subject.kind, subject_id: input.subject.id,
        responsibility: input.responsibility, user_id: input.user_id, effective_from: from, effective_to: to,
        assigned_by: input.actor_user_id,
    };
    try {
        await db.batch([
            insertStatement(row, now.toISOString()),
            logStatement({
                workspace_id: row.workspace_id, assignment_id: row.assignment_id, event: 'assigned',
                subject_kind: row.subject_kind, subject_id: row.subject_id, responsibility: row.responsibility,
                user_id: row.user_id, actor_user_id: input.actor_user_id,
                reason: gate === 'solo' ? (reason ? `${SOLO_SELF_GRANT}: ${reason}` : SOLO_SELF_GRANT) : reason,
                before_json: null, after_json: snapshot(row), at: now.toISOString(),
            }, logId),
        ]);
    }
    catch (err) {
        if (/unique|duplicate/i.test(String(err?.message ?? err))) {
            throw new AssignmentConflictError(`${input.user_id} already holds ${input.responsibility} on ${input.subject.kind}:${input.subject.id}`);
        }
        throw err;
    }
    if (gate === 'solo')
        await recordSoloSelfGrant(db, row, now);
    await noticeHatChange(db, {
        workspace_id: input.workspace_id, subject: input.subject, responsibility: input.responsibility, notify: priorHolders.filter((u) => u !== row.user_id),
        change: 'added', other_user_id: row.user_id, actor_user_id: input.actor_user_id, assignment_id: row.assignment_id, now,
    });
    return loadAssignment(db, input.workspace_id, row.assignment_id);
}
/**
 * Idempotent assign for automatic writers (a service that already records an
 * owner/reviewer column): returns the open assignment if one exists.
 */
export async function ensureAssignment(db, input) {
    const existing = await db.query(`SELECT * FROM assignments
      WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ? AND responsibility = ? AND user_id = ? AND effective_to IS NULL
      LIMIT 1`, [input.workspace_id, input.subject.kind, input.subject.id, input.responsibility, input.user_id]);
    if (existing.results[0])
        return existing.results[0];
    return assign(db, input);
}
/**
 * V98-1-1 follow-up: the owner-column writers (findings, recurring
 * obligations, security risks, system control states, register items) call
 * this after changing the column so hats stay in step with it. Ends the
 * previous holder's open assignment for this hat (only theirs — hats given
 * through /assignments are left alone) and ensures the new holder's.
 */
export async function syncOwnerAssignment(db, input) {
    const responsibility = input.responsibility ?? 'owner';
    const ended = [];
    const prev = input.previous_user_id ?? null;
    if (prev && prev !== input.user_id) {
        const open = await db.query(`SELECT * FROM assignments
        WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ? AND responsibility = ? AND user_id = ? AND effective_to IS NULL`, [input.workspace_id, input.subject.kind, input.subject.id, responsibility, prev]);
        for (const a of open.results) {
            ended.push(await endAssignment(db, {
                workspace_id: input.workspace_id, assignment_id: a.assignment_id, actor_user_id: input.actor_user_id,
                reason: input.reason ?? 'Owner changed', now: input.now, newId: input.newId,
            }));
        }
    }
    if (!input.user_id)
        return { ensured: null, ended };
    try {
        const ensured = await ensureAssignment(db, {
            workspace_id: input.workspace_id, subject: input.subject, responsibility, user_id: input.user_id,
            actor_user_id: input.actor_user_id, reason: input.reason, now: input.now, newId: input.newId,
        });
        return { ensured, ended };
    }
    catch (err) {
        if (input.bestEffort && (err instanceof AssignmentValidationError || err instanceof AssignmentRejectedError ||
            err instanceof AssignmentNotFoundError || err instanceof AssignmentConflictError)) {
            return { ensured: null, ended };
        }
        throw err;
    }
}
/** End an assignment now (the row stays; the log records who ended it and why). */
export async function endAssignment(db, input) {
    const reason = cleanReason(input.reason, false);
    const a = await loadAssignment(db, input.workspace_id, input.assignment_id);
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    if (a.effective_to !== null && a.effective_to <= nowIso) {
        throw new AssignmentConflictError(`Assignment ${a.assignment_id} has already ended`);
    }
    // A future-dated row (e.g. an auto-return) ends at its own start.
    const endAt = a.effective_from > nowIso ? a.effective_from : nowIso;
    const newId = input.newId ?? (() => generateRandomId('asl'));
    await db.batch([
        {
            sql: `UPDATE assignments SET effective_to = ? WHERE assignment_id = ? AND workspace_id = ?`,
            params: [endAt, a.assignment_id, input.workspace_id],
        },
        logStatement({
            workspace_id: a.workspace_id, assignment_id: a.assignment_id, event: 'ended',
            subject_kind: a.subject_kind, subject_id: a.subject_id, responsibility: a.responsibility,
            user_id: a.user_id, actor_user_id: input.actor_user_id, reason,
            before_json: snapshot(a), after_json: snapshot({ ...a, effective_to: endAt }), at: nowIso,
        }, newId),
    ]);
    await noticeHatChange(db, {
        workspace_id: a.workspace_id, subject: { kind: a.subject_kind, id: a.subject_id }, responsibility: a.responsibility, notify: [a.user_id],
        change: 'ended', other_user_id: null, actor_user_id: input.actor_user_id, assignment_id: a.assignment_id, now,
    });
    return loadAssignment(db, input.workspace_id, a.assignment_id);
}
/**
 * T16: move a set of hats from one holder to another in one batch (all or
 * nothing). Every moved hat writes a `reassigned` log row with before/after.
 * TODO(V98-1-9): re-run the SoD pair check for the receiver.
 */
export async function reassignHats(db, input) {
    const reason = cleanReason(input.reason, true);
    if (!Array.isArray(input.subjects) || input.subjects.length === 0) {
        throw new AssignmentValidationError('no_subjects', 'At least one subject is required');
    }
    if (input.subjects.length > REASSIGN_MAX_SUBJECTS) {
        throw new AssignmentValidationError('too_many_subjects', `At most ${REASSIGN_MAX_SUBJECTS} subjects per reassignment`);
    }
    input.subjects.forEach(validateSubject);
    if (input.responsibility !== undefined && !isAssignmentResponsibility(input.responsibility)) {
        throw new AssignmentValidationError('invalid_responsibility', `Unknown responsibility: ${String(input.responsibility)}`);
    }
    if (input.from_user_id === input.to_user_id) {
        throw new AssignmentValidationError('same_user', 'Reassign to a different person');
    }
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    const until = input.until != null ? toIso(input.until, 'until') : null;
    if (until !== null && until <= nowIso) {
        throw new AssignmentValidationError('invalid_window', 'until must be in the future');
    }
    const newId = input.newId ?? (() => generateRandomId('asg'));
    const logId = input.newId ?? (() => generateRandomId('asl'));
    // Dedupe subjects; collect the hats being moved.
    const seen = new Set();
    const subjects = input.subjects.filter((s) => {
        const k = `${s.kind}\u0000${s.id}`;
        if (seen.has(k))
            return false;
        seen.add(k);
        return true;
    });
    const moving = [];
    for (const subject of subjects) {
        const current = (await listAssignments(db, input.workspace_id, subject, { now }))
            .filter((a) => a.user_id === input.from_user_id && (input.responsibility === undefined || a.responsibility === input.responsibility));
        if (current.length === 0) {
            throw new AssignmentNotFoundError('assignment', `${subject.kind}:${subject.id} held by ${input.from_user_id}`);
        }
        moving.push(...current);
    }
    await assertAssignable(db, {
        workspace_id: input.workspace_id, user_id: input.to_user_id, actor_user_id: input.actor_user_id,
        subjects: moving.map((a) => ({ subject: { kind: a.subject_kind, id: a.subject_id }, responsibility: a.responsibility })),
        now, newId: logId,
    });
    const gate = await selfGrantGate(db, input.workspace_id, input.actor_user_id, input.to_user_id, moving.map((a) => a.responsibility));
    const logReason = gate === 'solo' ? `${SOLO_SELF_GRANT}: ${reason}` : reason;
    const statements = [];
    const createdIds = [];
    const returnIds = [];
    for (const a of moving) {
        const clash = await db.query(`SELECT assignment_id FROM assignments
        WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ? AND responsibility = ? AND user_id = ? AND effective_to IS NULL
        LIMIT 1`, [input.workspace_id, a.subject_kind, a.subject_id, a.responsibility, input.to_user_id]);
        if (clash.results[0]) {
            throw new AssignmentConflictError(`${input.to_user_id} already holds ${a.responsibility} on ${a.subject_kind}:${a.subject_id}`);
        }
        const created = {
            assignment_id: newId(), workspace_id: input.workspace_id, subject_kind: a.subject_kind, subject_id: a.subject_id,
            responsibility: a.responsibility, user_id: input.to_user_id, effective_from: nowIso, effective_to: until,
            assigned_by: input.actor_user_id,
        };
        const back = until === null ? null : {
            ...created, assignment_id: newId(), user_id: a.user_id, effective_from: until, effective_to: null,
        };
        statements.push({
            sql: `UPDATE assignments SET effective_to = ? WHERE assignment_id = ? AND workspace_id = ? AND effective_to IS NULL`,
            params: [nowIso, a.assignment_id, input.workspace_id],
        }, insertStatement(created, nowIso));
        if (back)
            statements.push(insertStatement(back, nowIso));
        statements.push(logStatement({
            workspace_id: input.workspace_id, assignment_id: created.assignment_id, event: 'reassigned',
            subject_kind: a.subject_kind, subject_id: a.subject_id, responsibility: a.responsibility,
            user_id: input.to_user_id, actor_user_id: input.actor_user_id, reason: logReason,
            before_json: snapshot(a),
            after_json: JSON.stringify({
                ...JSON.parse(snapshot(created)), from_user_id: a.user_id, until, return_assignment_id: back?.assignment_id ?? null,
            }),
            at: nowIso,
        }, logId));
        createdIds.push(created.assignment_id);
        if (back)
            returnIds.push(back.assignment_id);
    }
    try {
        await db.batch(statements);
    }
    catch (err) {
        if (/unique|duplicate/i.test(String(err?.message ?? err))) {
            throw new AssignmentConflictError('A concurrent change already moved one of these assignments');
        }
        throw err;
    }
    const load = (ids) => Promise.all(ids.map((id) => loadAssignment(db, input.workspace_id, id)));
    const created = await load(createdIds);
    for (let i = 0; i < moving.length; i++) {
        const a = moving[i];
        if (gate === 'solo' && SENSITIVE_HATS.has(a.responsibility))
            await recordSoloSelfGrant(db, created[i], now);
        await noticeHatChange(db, {
            workspace_id: input.workspace_id, subject: { kind: a.subject_kind, id: a.subject_id }, responsibility: a.responsibility, notify: [a.user_id],
            change: 'moved', other_user_id: input.to_user_id, actor_user_id: input.actor_user_id, assignment_id: created[i].assignment_id, now,
        });
    }
    return {
        ended: await load(moving.map((a) => a.assignment_id)),
        created,
        returns: await load(returnIds),
    };
}
//# sourceMappingURL=assignment-service.js.map